import { useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Button } from '../../components/ui/button';
import {
  portalThumbnailUrl,
  THUMBNAIL_ACCEPT,
  validateThumbnail,
} from '../../lib/portalLibrary/thumbnail';
import type { PortalLibraryEntry } from '../../types/portalLibrary';
import { removePortalThumbnail, uploadPortalThumbnail } from './api';

// A tile's cover image (Round 4 §4). ⚠️ The same `validateThumbnail` the server
// runs is run here first, as a courtesy — the server re-checks the BYTES and is
// the gate. ⚠️ Rule 12: a cover image, never a client's logo or brand artwork.

function toBase64(bytes: Uint8Array): string {
  let binary = '';
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return btoa(binary);
}

export function ThumbnailControl({
  entry,
  onChanged,
}: {
  entry: PortalLibraryEntry;
  onChanged: (entry: PortalLibraryEntry) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);
  const url = portalThumbnailUrl(entry.id, entry.thumbnailPath);

  const upload = useMutation({
    mutationFn: async (file: File) => {
      const bytes = new Uint8Array(await file.arrayBuffer());
      const check = validateThumbnail({ filename: file.name, declaredType: file.type, bytes });
      if (!check.ok) throw new Error(check.message);
      return uploadPortalThumbnail(entry.id, {
        filename: file.name,
        contentType: file.type,
        dataBase64: toBase64(bytes),
      });
    },
    onSuccess: (e) => {
      setError(null);
      onChanged(e);
    },
    onError: (err: Error) => setError(err.message),
  });
  const remove = useMutation({
    mutationFn: () => removePortalThumbnail(entry.id),
    onSuccess: onChanged,
    onError: (err: Error) => setError(err.message),
  });

  return (
    <div className="space-y-2">
      {url ? (
        <img src={url} alt="" className="h-24 w-40 rounded border border-slate-200 object-cover" />
      ) : (
        <p className="text-xs text-slate-500">No image — the tile shows its title in Kyck colours.</p>
      )}
      <div className="flex gap-2">
        <input
          ref={inputRef}
          type="file"
          accept={THUMBNAIL_ACCEPT}
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) upload.mutate(f);
            e.target.value = '';
          }}
        />
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={upload.isPending}
          onClick={() => inputRef.current?.click()}
        >
          {upload.isPending ? 'Uploading…' : url ? 'Replace image' : 'Upload image'}
        </Button>
        {url && (
          <Button type="button" size="sm" variant="ghost" disabled={remove.isPending} onClick={() => remove.mutate()}>
            Remove image
          </Button>
        )}
      </div>
      <p className="text-xs text-slate-500">
        PNG, JPEG or WebP, up to 2 MB. Use a cover image — never a client’s logo or brand artwork.
        Saved straight away.
      </p>
      {error && <p className="text-sm text-destructive">{error}</p>}
    </div>
  );
}
